"use client";
import clsx from "clsx";
import { getImageProps } from "next/image";

const HeroSection = ({
  mainTitle,
  title,
  mobileTitle,
  showNavigation = true,
  hasHeaderNavigation = false,
  navigationComponent: NavigationComponent,
  searchComponent: SearchComponent,
  backgroundImage = {
    desktop: "",
    mobile: "",
    alt: "",
  },
  className = "",
  titleContainerClassName = "",
  contentContainerClassName = "container",
  containerClassName = "",
  contentClassName = "",
  titleClassName = "",
  mobileTitleClassName = "",
  navigationClassName = "",
  searchClassName = "",
  backgroundImageClassName = "",
  backgroundWrapperClassName = "",
  backgroundClassName = {
    desktop: "",
    mobile: "",
  },
  searchComponentProps = {},
}) => {
  return (
    <section
      className={clsx(
        "bg-background-global relative z-[12] w-full pb-4 md:flex md:items-start md:justify-end md:pt-[38px]",
        hasHeaderNavigation
          ? "pt-42 min-h-[400px] md:min-h-[510px]"
          : "pt-[76px] min-h-[350px] md:min-h-auto md:pb-[38px]",
        className,
      )}
    >
      <div
        className={clsx(
          "relative z-10 flex h-full w-full items-center justify-center",
          containerClassName,
        )}
      >
        <div className={contentContainerClassName}>
          <div
            className={clsx(
              "flex flex-col gap-4 md:gap-[38px]",
              contentClassName,
            )}
          >
            {/* Title Section */}
            {mainTitle && (
              <div className={titleContainerClassName}>{mainTitle}</div>
            )}
            {(title || mobileTitle) && !mainTitle && (
              <div className={titleContainerClassName}>
                {title && (
                  <h1
                    className={clsx(
                      "hidden text-left text-[44px] leading-[50px] font-semibold whitespace-pre-line md:block",
                      titleClassName,
                    )}
                  >
                    {title}
                  </h1>
                )}
                {mobileTitle && (
                  <h1
                    className={clsx(
                      "text-left text-xl font-semibold whitespace-pre-line md:hidden",
                      mobileTitleClassName,
                    )}
                  >
                    {mobileTitle}
                  </h1>
                )}
              </div>
            )}

            {/* Navigation Section */}
            {showNavigation && NavigationComponent && (
              <div
                className={clsx(
                  "hidden gap-8 md:inline-flex",
                  navigationClassName,
                )}
              >
                <NavigationComponent />
              </div>
            )}

            {/* Search/Content Section */}
            {SearchComponent && (
              <div className={searchClassName}>
                <SearchComponent {...searchComponentProps} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Background Images */}
      {(backgroundImage.desktop || backgroundImage.mobile) && (
        <HeroBackgroundImage
          backgroundImage={backgroundImage}
          backgroundWrapperClassName={backgroundWrapperClassName}
          backgroundImageClassName={backgroundImageClassName}
          backgroundClassName={backgroundClassName}
        />
      )}
    </section>
  );
};

function HeroBackgroundImage({
  backgroundImage,
  backgroundWrapperClassName,
  backgroundImageClassName,
  backgroundClassName,
}) {
  const commonProps = {
    alt: backgroundImage.alt || "turna.com",
    quality: 85,
  };

  // Mobile-first fallback — preload scanner fetches smaller image on mobile
  const fallbackSrc = backgroundImage.mobile || backgroundImage.desktop;
  const { props: fallbackProps } = getImageProps({
    ...commonProps,
    src: fallbackSrc,
    width: 693,
    height: 549,
  });

  // Only create separate desktop source when mobile is the fallback
  const desktopProps =
    backgroundImage.desktop && backgroundImage.mobile
      ? getImageProps({
          ...commonProps,
          src: backgroundImage.desktop,
          width: 693,
          height: 549,
        }).props
      : null;

  const tabletProps = backgroundImage.tablet
    ? getImageProps({
        ...commonProps,
        src: backgroundImage.tablet,
        width: 600,
        height: 475,
      }).props
    : null;

  const mobileProps = backgroundImage.mobile
    ? getImageProps({
        ...commonProps,
        src: backgroundImage.mobile,
        width: 402,
        height: 549,
      }).props
    : null;

  return (
    <picture
      className={clsx(
        "absolute top-0 h-full w-auto ltr:right-0 rtl:left-0 rtl:scale-x-[-1]",
        backgroundWrapperClassName,
      )}
    >
      {tabletProps && (
        <source
          media="(min-width: 768px) and (max-width: 1199px)"
          srcSet={tabletProps.srcSet}
        />
      )}
      {desktopProps && (
        <source media="(min-width: 1200px)" srcSet={desktopProps.srcSet} />
      )}
      {mobileProps && (
        <source media="(max-width: 767px)" srcSet={mobileProps.srcSet} />
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        {...fallbackProps}
        className={clsx(
          "h-full w-auto object-cover",
          backgroundImageClassName,
          backgroundClassName.desktop || backgroundClassName.mobile,
        )}
        loading="eager"
        fetchPriority="high"
      />
    </picture>
  );
}

export default HeroSection;
